/**
 * Счётчики модемов по списку портов — та же логика, что npStats в NetworkPorts.
 */

/** @param {unknown} modemsStatusResponse */
export const extractModemsStatusList = (modemsStatusResponse) => {
  if (modemsStatusResponse?.data?.data?.modems && Array.isArray(modemsStatusResponse.data.data.modems)) {
    return modemsStatusResponse.data.data.modems;
  }
  if (modemsStatusResponse?.data?.modems && Array.isArray(modemsStatusResponse.data.modems)) {
    return modemsStatusResponse.data.modems;
  }
  if (modemsStatusResponse?.modems && Array.isArray(modemsStatusResponse.modems)) {
    return modemsStatusResponse.modems;
  }
  return [];
};

const isPortEligibleForModemStats = (port) => {
  const connectionIsDisabled =
    port?.connection_active === false ||
    port?.connection_enabled === false ||
    String(port?.connection_status || '').toLowerCase() === 'disconnected';
  const modemIsOffline = port?.modem_offline === true;
  return !connectionIsDisabled && !modemIsOffline;
};

/**
 * Порты с modem_status (как NetworkPorts перед npStats).
 * @param {unknown[]} portsData
 * @param {unknown[]} modemsStatusData
 */
export const buildPortsWithModemStatus = (portsData, modemsStatusData) => {
  if (!Array.isArray(portsData)) {
    return [];
  }

  const modemsStatusMap = {};
  (Array.isArray(modemsStatusData) ? modemsStatusData : []).forEach((modem) => {
    if (modem?.modem_id != null) {
      modemsStatusMap[String(modem.modem_id)] = modem;
    }
  });

  const portsForDisplay = portsData.filter(isPortEligibleForModemStats);

  return portsForDisplay.map((port) => {
    let modemStatus = null;
    if (port.server_id !== undefined && port.server_id !== null) {
      modemStatus = modemsStatusMap[String(port.server_id)];
    }
    if (!modemStatus && port.kraken_id !== undefined && port.kraken_id !== null) {
      modemStatus = modemsStatusMap[String(port.kraken_id)];
    }
    if (!modemStatus) {
      modemStatus = (Array.isArray(modemsStatusData) ? modemsStatusData : []).find((m) => {
        const mId = m.modem_id ? String(m.modem_id) : null;
        const sId = port.server_id != null ? String(port.server_id) : null;
        const kId = port.kraken_id != null ? String(port.kraken_id) : null;
        const kmId = port.kraken_modem_id != null ? String(port.kraken_modem_id) : null;
        return mId === sId || mId === kId || mId === kmId;
      });
    }
    if (!modemStatus && port.name && port.connection_name) {
      modemStatus = (Array.isArray(modemsStatusData) ? modemsStatusData : []).find(
        (m) => m.modem_name === port.name && m.connection_name === port.connection_name
      );
    }
    return {
      ...port,
      modem_status: modemStatus?.status || port.modem_status || 'unknown',
    };
  });
};

/** @param {unknown[] | null | undefined} ports */
export const computeModemStatsFromPorts = (ports) => {
  if (!Array.isArray(ports)) {
    return null;
  }

  const statsPorts = ports.filter((p) => p.modem_offline !== true);
  const total_modems = statsPorts.length;
  const available_modems = statsPorts.filter((p) => p.modem_status === 'working').length;
  const unavailable_modems = statsPorts.filter(
    (p) => p.modem_status === 'not_working' || p.modem_status === 'unavailable'
  ).length;
  const unknown_modems = statsPorts.filter(
    (p) => !p.modem_status || p.modem_status === 'unknown'
  ).length;

  return { total_modems, available_modems, unavailable_modems, unknown_modems };
};

/**
 * Счётчики модемов из /api/kraken-connections (servers_count) — источник истины для total.
 * @param {unknown[] | null | undefined} connections
 */
export const computeModemStatsFromConnections = (connections) => {
  if (!Array.isArray(connections) || connections.length === 0) {
    return null;
  }

  const activeConnections = connections.filter((conn) => conn && conn.is_active !== false);
  const total_modems = activeConnections.reduce(
    (sum, conn) => sum + Number(conn.servers_count ?? conn.modems_count ?? 0),
    0
  );
  const unavailable_modems = activeConnections.reduce(
    (sum, conn) => sum + Number(conn.unavailable_modems ?? 0),
    0
  );
  const available_modems = Math.max(0, total_modems - unavailable_modems);

  return { total_modems, available_modems, unavailable_modems };
};

/**
 * Для ProxyStatsPanel: total/unavailable/available — из connections API;
 * unknown_modems — из пересчёта портов (если есть).
 * @param {unknown[] | null | undefined} connections
 * @param {unknown[] | null | undefined} ports
 */
export const mergeModemStatsForPanel = (connections, ports) => {
  const fromConnections = computeModemStatsFromConnections(connections);
  const fromPorts = computeModemStatsFromPorts(ports);

  if (!fromConnections && !fromPorts) {
    return null;
  }

  if (!fromConnections) {
    return fromPorts;
  }

  return {
    ...fromConnections,
    unknown_modems: fromPorts?.unknown_modems ?? 0,
  };
};

/** Достаёт JSON-объект статистики из ответа axios /api/stats. */
export const parseStatsPayloadFromApi = (axiosResponse) => {
  if (!axiosResponse || typeof axiosResponse !== 'object') {
    return null;
  }
  const raw = axiosResponse.data !== undefined ? axiosResponse.data : axiosResponse;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  if (typeof raw.data === 'object' && raw.data !== null && !Array.isArray(raw.data)) {
    const inner = raw.data;
    if ('total_proxies' in inner || 'available_modems' in inner || 'total_modems' in inner) {
      return inner;
    }
  }
  return raw;
};
